<template>
  <div class="cb-dynamic-data-table-raw" :style="componentStyle">
    <template v-if="tableData.length">
      <GroupedRawDynamicDataTable
        v-if="hasGrouping"
        :break-groups-on-new-page="props.data?.break_groups_on_new_page !== false"
        :canvas-metadata="props.canvasMetadata"
        :component-id="props.id!"
        :data="props.data!"
        :group-header-background-color="groupHeaderBackgroundColor"
        :header-background-color="headerBackgroundColor"
        :parent-type="parentType"
        :style="isScribbleChild ? pick(style, 'width', 'height') : {}"
        :table-data="tableData"
        @sortChanged="(sorting) => handleSortChanged(sorting ? [sorting] : [])"
      />
      <SimpleDynamicDataTable
        v-else
        class="!pa-0"
        :class="{
          'cb-dynamic-data-table-raw__column': data?.layout === 'column',
          'cb-dynamic-data-table-raw__row': data?.layout === 'row',
        }"
        :columns="columns"
        :data="props.data"
        :header-background-color="headerBackgroundColor"
        :header-foreground-color="headerForegroundColor"
        :hide-page-selector="hidePageSelector"
        :hide-pagination="hidePagination"
        :hide-total-results="true"
        :initial-sorting="initialSorting?.[0]"
        :page-size="pageSize"
        :parent-type="parentType"
        :style="isScribbleChild ? pick(style, 'width', 'height') : {}"
        :table-data="tableData"
        @sortChanged="handleSortChanged"
      />
    </template>
    <div v-else class="text-center p-4 text-primary3"></div>
  </div>
</template>

<script setup lang="ts">
import { type ColumnSort, type SortingState } from '@tanstack/table-core'
import { get, isEmpty, omit, pick } from 'lodash-es'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import SimpleDynamicDataTable from './DynamicDataTable.raw.simple.vue'
import GroupedRawDynamicDataTable from './DynamicDataTable.raw.grouped.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useThemeVars from '@lib/composables/useThemeVars'

import { DEFAULT_FONT } from '@canvas-builder/constants'
import {
  type CanvasTheme,
  ComponentTypes,
  type ComponentNode,
  type DynamicDataTableProps,
} from '@canvas-builder/types/canvas'
import type { QueryAttribute } from '@lib/types/query-builder.types'
import type { CanvasMetadataRetrieve } from '@lib/types/canvases'
import { getContrastTextColor } from '@lib/util/color'
import { getQueryBuilderFilterIteratee } from '@lib/util/query-builder.util'
import type { CanvasRetrieve } from '@openapi'

const props = defineProps<{
  data?: DynamicDataTableProps
  parentType?: ComponentNode['type']
  selected?: boolean
  style?: Record<string, any>
  theme_meta?: ComponentNode['theme_meta']
  id?: string
  canvasMetadata?: CanvasMetadataRetrieve['config']
  usedInSectionId?: CanvasRetrieve['id']
}>()

const emit = defineEmits<{
  sortChanged: [s: ColumnSort | null]
  style: [s: Record<string, any>]
}>()

const { t } = useI18n()
const { activeCanvas, activeCanvasSections, isCanvas, isPrintDisplayMode } = useCanvas()
const { getCssThemeMetaStyleOverrides, getComponentSpecificThemeMetaOverrides } = useComponentStyle()
const themeVars = useThemeVars()
const { crmShape } = useCrmShape()

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})

const componentStyle = computed(() => {
  const baseStyle = {
    ...props.style,
    ...getCssThemeMetaStyleOverrides(ComponentTypes.DynamicDataTable, props?.theme_meta, sectionTheme.value),
  }

  if (isScribbleChild.value) {
    return omit(baseStyle, 'top', 'left', 'width', 'height')
  }

  return baseStyle
})

const contextData = computed(() => {
  const context =
    !isEmpty(activeCanvas.value?.context) || isCanvas.value
      ? { ...crmShape.value, ...activeCanvas.value?.context }
      : crmShape.value

  if (!props.data?.path || !context) return []

  const rawData = get(context, props.data.path, [])

  if (!props.data?.filters?.length) return rawData

  const filterFn = getQueryBuilderFilterIteratee(props.data.filters as QueryAttribute[])

  return rawData.filter(filterFn)
})

const fontFamily = computed(() => {
  return props.data?.theme?.font_family ?? DEFAULT_FONT
})

const headerBackgroundColor = computed(() => {
  const themeMetaStyle = getComponentSpecificThemeMetaOverrides(ComponentTypes.DynamicDataTable, props?.theme_meta)

  return themeMetaStyle.headerBackgroundColor ?? props.data?.theme?.background_color ?? themeVars.value.primary6
})

const groupHeaderBackgroundColor = computed(() => {
  const themeMetaStyle = getComponentSpecificThemeMetaOverrides(ComponentTypes.DynamicDataTable, props?.theme_meta)

  return themeMetaStyle.groupHeaderBackgroundColor ?? props.data?.theme?.group_header_color ?? themeVars.value.primary6
})

const headerForegroundColor = computed(() => {
  return getContrastTextColor(headerBackgroundColor.value)
})

const hidePageSelector = computed(() => {
  return !!(
    tableData.value?.length &&
    paginationConfig.value?.pageSize &&
    tableData.value?.length <= paginationConfig.value?.pageSize
  )
})

const hidePagination = computed(() => {
  return isPrintDisplayMode.value || isEmpty(paginationConfig.value)
})

const initialSorting = computed(() => {
  if (props.data?.layout === 'row') return undefined
  return props.data?.properties
    ?.filter((prop) => prop.sort?.default)
    .map((prop) => ({ id: prop.key, desc: prop.sort?.direction === 'desc' }))
})

const pageSize = computed(() => {
  return isPrintDisplayMode.value ? undefined : paginationConfig.value.pageSize
})

const paginationConfig = computed(() => {
  if (props.data?.layout === 'column' && props.data?.page_size) {
    return {
      pageSize: props.data.page_size,
    }
  }
  return {}
})

const sectionTheme = computed(() => {
  return props.usedInSectionId ? (activeCanvasSections.value[props.usedInSectionId]?.theme as CanvasTheme) : undefined
})

const tableData = computed(() => {
  if (!Array.isArray(contextData.value)) return []

  if (props.data?.layout === 'row') {
    // Transform data for row layout
    const rowData =
      props.data.properties
        ?.filter((prop) => !prop.group)
        ?.map((prop) => {
          const row: Record<string, any> = {
            key: prop.key,
            property: prop.key,
          }
          contextData.value.forEach((item: any, index: number) => {
            row[`value${index}`] = get(item, prop.key)
          })
          return row
        }) || []
    return rowData
  }

  // Transform data for column layout
  const columnData = contextData.value.map((item, index) => {
    const row: Record<string, any> = { key: index }
    props.data?.properties?.forEach((prop) => {
      row[prop.key] = get(item, prop.key)
    })
    return row
  })

  return columnData
})

const hasGrouping = computed(() => !!props.data?.properties?.find((prop) => prop.group))

const columns = computed(() => {
  if (!props.data?.properties) return []

  // Filter out properties marked for grouping
  const visibleProperties = props.data.properties
    .filter((prop) => !prop.group)
    .filter((prop) => !isPrintDisplayMode.value || prop.printable !== false)
  const groupProperty = props.data?.properties?.find((prop) => prop.group)

  if (props.data.layout === 'column') {
    const columnsArray = []

    // Add the grouped column first if grouping is enabled
    if (groupProperty) {
      columnsArray.push({
        id: groupProperty.key,
        header: t('canvasUI.canvasBuilder.dynamicDataTable.group'),
        enableSorting: false,
      })
    }

    // Add the rest of the columns
    columnsArray.push(
      ...visibleProperties.map((prop) => ({
        id: prop.key,
        header: prop.display_name || prop.key,
        enableSorting: prop.sort?.enabled && (!isPrintDisplayMode.value || prop.sort?.default),
        sortDescFirst: prop.sort?.direction === 'desc',
      })),
    )

    return columnsArray
  }

  // Row layout: property names in first column as headers
  return [
    {
      id: 'property',
      header: '',
      enableSorting: false,
    },
    ...contextData.value.map((_: any, index: number) => ({
      id: `value${index}`,
      header: '',
      enableSorting: false,
    })),
  ]
})

function handleSortChanged(sorting: SortingState | ColumnSort | null) {
  if (Array.isArray(sorting)) {
    emit('sortChanged', !sorting?.length ? null : sorting?.[0])
  } else {
    emit('sortChanged', sorting)
  }
}
</script>

<style lang="scss" scoped>
.cb-dynamic-data-table-raw {
  :deep(table) {
    table-layout: auto !important;
    font-family: v-bind('fontFamily');
  }
}

@media print {
  .cb-dynamic-data-table-raw {
    :deep(th),
    :deep(td),
    :deep(tr) {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
    }
  }
}
</style>
